import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { masterApi, apiError, formatPhone } from "@/lib/api";
import { useUnit } from "@/pages/Master/MasterApp";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ExcelBtn } from "@/lib/excelDownload";
import { modal } from "@/lib/modal";
import { protegeDoModalGlobal } from "@/lib/dialogModal";
import { inserirNoCursor } from "@/lib/indicacaoWeb";
import { smsInfo, textoTamanhoSms, temEmoji, SMS_LIMITE } from "@/lib/mensagensAuto";
import { CATEGORIAS, CANAL_ENVIO_LBL, motivosExclusao, resumoPreview, estimativaSms } from "@/lib/campanhas";
import { inicioDiaBR, fimDiaBR, atalhoPeriodoBR, hojeBR, somaDiasBR } from "@/lib/periodoBR";
import { comPagina, normalizaPaginado } from "@/lib/paginacao";
import Pagination from "@/components/crud/Pagination";
import {
  ETAPAS_LISTA, funilRota, frentistasEmAlerta, textoRazao, textoPct, fmtDia, reais, centavosLitro,
  rotuloEtapaCliente, resumoConfigRota, CATEGORIA_LBL, fmtDataHora,
  VARIAVEIS_ENVIO_ROTA, VARIAVEIS_ENVIO_ROTA_DESCRICAO, LIMITE_SELECIONADOS_ROTA, TEXTO_ENVIO_ROTA_PADRAO,
  validarTextoEnvioRota, corpoEnvioRota, textoPublicoEnvio, erroEnvioRota,
} from "@/lib/rotaFidelizacao";
import { Route, AlertTriangle, Settings, Users, Car, Send } from "lucide-react";

/**
 * Fase 3 (pedido de 19/10, item 10) — Rota de Fidelização no Master.
 * Liga/desliga global (master_config.rota_fidelizacao_ativa, PUT /master/config), totais e funil
 * por etapa, % de conclusão, alerta de frentista que inscreve muito acima da média dos colegas,
 * lista paginada por etapa com Excel e os valores configurados por unidade e categoria (editados
 * nas telas de Descontos, seção "Rota de Fidelização").
 * "Etapa N" = cliente que vai receber o desconto da etapa N no próximo abastecimento.
 *
 * Envio manual (verificação final da Fase 5, item 10): "Enviar mensagem" (SMS/WhatsApp) para os
 * clientes selecionados na lista ou para todos da etapa/unidade, com prévia (quem recebe e quem fica
 * de fora) e confirmação. Os envios aparecem em Campanhas > Histórico de envios.
 *
 * Dois recortes (revisão das Fases 3 e 4): a SITUAÇÃO ATUAL (quantos estão em cada etapa, concluídos,
 * prêmio disponível/usado e a lista por etapa) não segue o período; o PERÍODO DE INGRESSO (abre em
 * Hoje, padrão do dono) vale só para a coorte: inscritos, funil e % de conclusão. Como a Rota vale
 * 1 abastecimento por dia, a coorte de hoje nunca teria ninguém na Etapa 3 nem concluído.
 */

const ATALHOS = [["hoje", "Hoje"], ["ontem", "Ontem"], ["7d", "7 dias"], ["30d", "30 dias"], ["mes", "Este mês"], ["tudo", "Desde o início"]];

export default function MasterRotaFidelizacao() {
  const { unitId, unidades } = useUnit();
  const [ativa, setAtiva] = useState(null);           // null = carregando
  const [salvandoToggle, setSalvandoToggle] = useState(false);
  // Período de INGRESSO na Rota (padrão do dono: abre em Hoje). Vazio = desde o início.
  const inicial = atalhoPeriodoBR("hoje");
  const [inicio, setInicio] = useState(inicial.inicio);
  const [fim, setFim] = useState(inicial.fim);
  const [painel, setPainel] = useState(null);         // coorte: ingressaram no período
  const [atual, setAtual] = useState(null);           // situação atual (sem período)
  const [etapa, setEtapa] = useState("1");
  const [lista, setLista] = useState({ items: [], total: 0, page: 1, total_pages: 0 });
  const [valores, setValores] = useState(null);      // [{unit_id, unit_nome, categoria, ...resumo}]
  const [selecionados, setSelecionados] = useState({}); // envio manual: {cliente_id: nome}
  const [envioAberto, setEnvioAberto] = useState(false);

  const unitParam = unitId && unitId !== "all" ? unitId : null;
  const ini = inicio ? inicioDiaBR(inicio) : null;
  const fi = fim ? fimDiaBR(fim) : null;
  const periodoValido = (!inicio && !fim) || !!(ini && fi && inicio <= fim);
  const atalhoAtivo = !inicio && !fim ? "tudo"
    : (ATALHOS.find(([k]) => k !== "tudo" && atalhoPeriodoBR(k).inicio === inicio && atalhoPeriodoBR(k).fim === fim) || [null])[0];

  const semPeriodo = !inicio && !fim;
  // Coorte: unidade da inscrição + período de ingresso.
  const qsPainel = () => {
    const p = new URLSearchParams();
    if (unitParam) p.set("unit_id", unitParam);
    if (ini) p.set("inicio", ini);
    if (fi) p.set("fim", fi);
    return p.toString();
  };
  // Situação atual: só a unidade da inscrição (não segue o período).
  const qsAtual = () => {
    const p = new URLSearchParams();
    if (unitParam) p.set("unit_id", unitParam);
    return p.toString();
  };
  // A lista por etapa é situação atual: só a unidade da inscrição (não segue o período).
  const qsLista = () => {
    const p = new URLSearchParams({ etapa });
    if (unitParam) p.set("unit_id", unitParam);
    return p.toString();
  };

  // Liga/desliga: config pública (mesma fonte da pista).
  useEffect(() => {
    masterApi.get("/master/config-publica")
      .then((r) => setAtiva(!!r?.data?.rota_fidelizacao_ativa))
      .catch(() => setAtiva(false));
  }, []);

  // Situação atual (sem período): totais por etapa, prêmio, alerta por frentista e liga/desliga.
  // Troca rápida de filtros: só a resposta mais recente entra na tela.
  const seqAtual = useRef(0);
  const carregarAtual = async () => {
    const minha = ++seqAtual.current;
    try {
      const qs = qsAtual();
      const r = await masterApi.get(`/master/rota-fidelizacao/painel${qs ? `?${qs}` : ""}`);
      if (minha !== seqAtual.current) return;
      setAtual(r.data || null);
      if (typeof r?.data?.ativa === "boolean") setAtiva(r.data.ativa);
    } catch (e) { if (minha === seqAtual.current) modal.error(apiError(e)); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregarAtual(); }, [unitId]);

  // Coorte do período de ingresso. "Desde o início" é a própria situação atual (sem 2ª consulta).
  const seqPainel = useRef(0);
  const carregarPainel = async () => {
    const minha = ++seqPainel.current;
    if (!periodoValido || semPeriodo) { setPainel(null); return; }
    try {
      const r = await masterApi.get(`/master/rota-fidelizacao/painel?${qsPainel()}`);
      if (minha !== seqPainel.current) return;
      setPainel(r.data || null);
    } catch (e) { if (minha === seqPainel.current) modal.error(apiError(e)); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregarPainel(); }, [unitId, inicio, fim]);

  const seqLista = useRef(0);
  const carregarLista = async (page = 1) => {
    const minha = ++seqLista.current;
    try {
      const r = await masterApi.get(comPagina(`/master/rota-fidelizacao/clientes?${qsLista()}`, page));
      if (minha !== seqLista.current) return;
      const pg = normalizaPaginado(r.data, page);
      setLista({ items: pg.items, total: pg.total, page: pg.page, total_pages: pg.total_pages });
    } catch (e) { if (minha === seqLista.current) modal.error(apiError(e)); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregarLista(1); }, [unitId, etapa]);
  // A seleção do envio manual vale para a aba/unidade em que foi feita.
  useEffect(() => { setSelecionados({}); }, [unitId, etapa]);

  // Valores configurados (rotas de config de descontos existentes) da unidade escolhida, ou de todas.
  useEffect(() => {
    let vivo = true;
    const alvo = unitParam ? unidades.filter((u) => u.id === unitParam) : unidades;
    if (!alvo.length) { setValores(unitParam ? [] : null); return undefined; }
    Promise.all(alvo.flatMap((u) => [
      masterApi.get(`/descontos/config?unit_id=${u.id}`)
        .then((r) => ({ unit_id: u.id, unit_nome: u.nome, categoria: "normal", ...resumoConfigRota(r.data) }))
        .catch(() => null),
      masterApi.get(`/app_driver/descontos/config?unit_id=${u.id}`)
        .then((r) => ({ unit_id: u.id, unit_nome: u.nome, categoria: "app_driver", ...resumoConfigRota(r.data) }))
        .catch(() => null),
    ])).then((linhas) => { if (vivo) setValores(linhas.filter(Boolean)); });
    return () => { vivo = false; };
  }, [unitParam, unidades]);

  const alternarRota = async (proximo) => {
    const ok = await modal.confirm({
      title: proximo ? "Ligar a Rota de Fidelização" : "Desligar a Rota de Fidelização",
      message: proximo
        ? "Com a Rota ligada, o frentista passa a ver a opção \"Incluir na Rota de Fidelização\" no cadastro novo (e no cliente que ainda não abasteceu). Quem está na Rota recebe só o desconto de cada etapa, vale 1 abastecimento por dia, e ganha o prêmio em saldo ao completar as 3 etapas. Confira antes os valores das etapas, do prêmio e do mínimo em cada unidade e categoria."
        : "Com a Rota desligada, ninguém novo entra na Rota e quem está no meio passa a seguir as regras normais de desconto enquanto ela estiver desligada. Ao religar, cada cliente retoma a etapa em que parou.",
      details: [
        { label: "Situação atual", value: ativa ? "LIGADA" : "DESLIGADA" },
        { label: "Passa a ficar", value: proximo ? "LIGADA" : "DESLIGADA" },
        { label: "Alcance", value: "Todas as unidades" },
      ],
      confirmText: proximo ? "Ligar a Rota" : "Desligar a Rota",
      tone: proximo ? undefined : "danger",
    });
    if (!ok) return;
    setSalvandoToggle(true);
    try {
      await masterApi.put("/master/config", { rota_fidelizacao_ativa: proximo });
      setAtiva(proximo);
      modal.success(proximo ? "Rota de Fidelização ligada." : "Rota de Fidelização desligada.");
      carregarAtual();
    } catch (e) { modal.error(apiError(e)); }
    finally { setSalvandoToggle(false); }
  };

  // Coorte = período de ingresso; sem período, a coorte é a própria situação atual.
  const coorte = semPeriodo ? atual : (periodoValido ? painel : null);
  const totais = atual?.totais || {};
  const totaisCoorte = coorte?.totais || {};
  const funil = funilRota(totaisCoorte);
  // Período só com ingressos de hoje/ontem: ninguém teve dias para fazer as 3 etapas (1 por dia).
  const soRecentes = !semPeriodo && periodoValido && !!inicio && inicio >= (somaDiasBR(hojeBR(), -1) || "");
  const frentistas = Array.isArray(atual?.inscricoes_por_frentista) ? atual.inscricoes_por_frentista : [];
  const alertas = frentistasEmAlerta(frentistas);
  // Regra do alerta (o backend devolve a que usou; sem ela, a da decisão do item 10).
  const regra = atual?.alerta_regra || {};
  const fatorAlerta = Number(regra.fator) || 1.5;
  const minimoAlerta = Number(regra.minimo) || 5;
  const janelaAlerta = Number(regra.janela_dias) || 30;
  const ocorrencias = Number(totaisCoorte.ocorrencias) || 0;
  const excelUrl = `/master/rota-fidelizacao/clientes/excel?${qsLista()}`;
  const etapaLbl = ETAPAS_LISTA.find(([k]) => k === etapa)?.[1] || etapa;
  const idsSelecionados = Object.keys(selecionados);
  const paginaToda = lista.items.length > 0 && lista.items.every((c) => selecionados[c.cliente_id] !== undefined);
  const marcar = (itens, ligar) => {
    const prox = { ...selecionados };
    itens.forEach((c) => { if (ligar) prox[c.cliente_id] = c.nome || ""; else delete prox[c.cliente_id]; });
    if (Object.keys(prox).length > LIMITE_SELECIONADOS_ROTA) {
      modal.error(`Selecione no máximo ${LIMITE_SELECIONADOS_ROTA} clientes por envio. Para mais, envie para toda a aba (sem marcar ninguém).`);
      return;
    }
    setSelecionados(prox);
  };
  const unidadeNome = unitParam ? (unidades.find((u) => u.id === unitParam)?.nome || "") : "";

  return (
    <div className="px-4 md:px-6 pt-4 pb-8 space-y-4 float-in" data-testid="master-rota">
      <div>
        <div className="chip mb-1.5 text-[10px] py-0.5">Gestão</div>
        <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1]">Rota de Fidelização</h1>
        <p className="text-[12px] text-neutral-500 mt-1">
          Cliente novo marcado pelo frentista no cadastro passa por 3 abastecimentos com desconto próprio
          (1 por dia) e ganha um prêmio em saldo no 4º. Depois segue as regras normais.
        </p>
      </div>

      {/* Liga/desliga global */}
      <section className="card-soft rounded-xl p-4 flex flex-wrap items-center justify-between gap-3" data-testid="rota-toggle-card">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-green-700 flex items-center justify-center shrink-0">
            <Route size={16} className="text-white" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm">Rota de Fidelização</span>
              {ativa != null && (
                <span data-testid="rota-situacao"
                      className={`chip text-[10px] py-0.5 font-bold uppercase tracking-wide ${ativa ? "chip-green" : "chip-gray"}`}>
                  {ativa ? "Ligada" : "Desligada"}
                </span>
              )}
            </div>
            <div className="text-[11px] text-neutral-500">
              Vale para todas as unidades. Nasce desligada; ligue depois de conferir os valores.
            </div>
          </div>
        </div>
        <Switch data-testid="rota-toggle" checked={!!ativa} disabled={ativa == null || salvandoToggle}
                onCheckedChange={(v) => alternarRota(!!v)} />
      </section>

      {/* Período de ingresso */}
      <div className="card-soft rounded-xl p-3 flex flex-wrap gap-3 items-end" data-testid="rota-filtros">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Ingresso de</label>
          <input type="date" data-testid="rota-inicio" value={inicio} onChange={(e) => setInicio(e.target.value)}
                 className="mt-1 h-9 rounded-md bg-white border border-neutral-200 px-2 text-sm block" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">até</label>
          <input type="date" data-testid="rota-fim" value={fim} onChange={(e) => setFim(e.target.value)}
                 className="mt-1 h-9 rounded-md bg-white border border-neutral-200 px-2 text-sm block" />
        </div>
        <div className="flex flex-wrap gap-1">
          {ATALHOS.map(([k, lbl]) => (
            <button key={k} data-testid={`rota-atalho-${k}`}
                    onClick={() => {
                      if (k === "tudo") { setInicio(""); setFim(""); return; }
                      const a = atalhoPeriodoBR(k); setInicio(a.inicio); setFim(a.fim);
                    }}
                    className={`h-9 px-2.5 text-[11px] font-medium ${atalhoAtivo === k ? "btn-primary-blue" : "btn-ghost"}`}>
              {lbl}
            </button>
          ))}
        </div>
        {!periodoValido && (
          <div className="text-[12px] text-rose-700 w-full" data-testid="rota-periodo-invalido">
            Informe as duas datas (início antes do fim) ou use "Desde o início".
          </div>
        )}
      </div>

      {/* Totais: coorte do período de ingresso */}
      <div className="space-y-1.5" data-testid="rota-totais-coorte">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500" data-testid="rota-totais-coorte-titulo">
          Ingressaram no período <span className="normal-case tracking-normal">(segue o período de ingresso acima)</span>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          <Kpi label="Inscritos" value={totaisCoorte.inscritos ?? "—"} sub="ingressaram no período" testid="rota-kpi-inscritos" />
          <Kpi label="% de conclusão" value={coorte ? textoPct(totaisCoorte.pct_conclusao) : "—"} highlight testid="rota-kpi-pct"
               sub={soRecentes
                 ? "ingressos de hoje/ontem ainda não podem ter concluído"
                 : "concluídos / inscritos do período"} />
        </div>
      </div>

      {/* Totais: situação atual (não segue o período) */}
      <div className="space-y-1.5" data-testid="rota-totais">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500" data-testid="rota-totais-atual-titulo">
          Situação atual <span className="normal-case tracking-normal">(não segue o período: todos os inscritos)</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
          <Kpi label="Na Etapa 1" value={totais.na_etapa_1 ?? "—"} sub="próximo: desconto da Etapa 1" testid="rota-kpi-etapa1" />
          <Kpi label="Na Etapa 2" value={totais.na_etapa_2 ?? "—"} sub="próximo: desconto da Etapa 2" testid="rota-kpi-etapa2" />
          <Kpi label="Na Etapa 3" value={totais.na_etapa_3 ?? "—"} sub="próximo: desconto da Etapa 3" testid="rota-kpi-etapa3" />
          <Kpi label="Concluídos" value={totais.concluidos ?? "—"} sub="completaram as 3 etapas" testid="rota-kpi-concluidos" />
          <Kpi label="Prêmio disponível" value={totais.premio_disponivel ?? "—"} testid="rota-kpi-premio-disp"
               sub={totais.premio_disponivel_reais != null ? `${reais(totais.premio_disponivel_reais)} a usar` : "ainda não usaram"} />
          <Kpi label="Prêmio usado" value={totais.premio_usado ?? "—"} testid="rota-kpi-premio-usado"
               sub={totais.premio_usado_reais != null ? `${reais(totais.premio_usado_reais)} abatidos` : "já usaram o prêmio"} />
        </div>
      </div>

      {ocorrencias > 0 && (
        <div data-testid="rota-ocorrencias" className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 text-[12px] text-amber-900 flex items-start gap-1.5">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <span>
            <b>{ocorrencias} venda{ocorrencias > 1 ? "s" : ""} com ocorrência na Rota</b> de clientes que ingressaram no período
            (2º uso no mesmo dia ou prêmio já usado, registrados sem travar a venda). Veja quais vendas em
            "Ocorrências da Rota", no fim da página.
          </span>
        </div>
      )}

      {/* min-w-0 nos filhos: sem ele a tabela alarga a trilha do grid e a página rola de lado no celular. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="rota-grid">
        {/* Funil */}
        <section className="card-soft rounded-xl p-4 space-y-2 min-w-0" data-testid="rota-funil">
          <h2 className="font-serif-display text-lg">Evolução na Rota</h2>
          <p className="text-[11px] text-neutral-500" data-testid="rota-funil-nota">
            Percentual sobre os clientes que ingressaram no período. A Rota vale 1 abastecimento por dia: quem
            ingressou hoje ou ontem ainda não pode ter concluído as 3 etapas.
          </p>
          {funil.map((f, i) => (
            <FunilPasso key={f.chave} label={f.label} valor={f.valor} pct={f.pct}
                        cor={i === 0 ? "emerald" : i < 3 ? "amber" : "blue"} testid={`rota-funil-${f.chave}`} />
          ))}
        </section>

        {/* Alerta de frentista */}
        <section className="card-soft rounded-xl p-4 space-y-2 min-w-0" data-testid="rota-frentistas">
          <div className="flex items-center gap-2">
            <Users size={15} className="text-rose-600" />
            <h2 className="font-serif-display text-lg">Inscrições por frentista</h2>
          </div>
          <p className="text-[11px] text-neutral-500" data-testid="rota-alerta-regra">
            Últimos {janelaAlerta} dias (não segue o período). Alerta quando o frentista inscreve mais de{" "}
            {fatorAlerta.toLocaleString("pt-BR")}x a média dos colegas da mesma unidade, com pelo menos {minimoAlerta} inscrições.
          </p>
          {alertas.length > 0 && (
            <div data-testid="rota-alerta" className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-[12px] text-rose-900 flex items-start gap-1.5">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>
                <b>{alertas.length} frentista{alertas.length > 1 ? "s" : ""}</b> inscrevendo muito acima da média dos colegas:{" "}
                {alertas.map((a) => a.frentista_nome || a.frentista_id).join(", ")}. Confira os cadastros.
              </span>
            </div>
          )}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-neutral-50">
                <TableHead className="text-[10px] uppercase tracking-widest">Frentista</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-center">Inscrições</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-center">Média colegas</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-center">Razão</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-center">Alerta</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {frentistas.map((f) => (
                  <TableRow key={`${f.frentista_id}-${f.unit_nome || ""}`} data-testid={`rota-frentista-${f.frentista_id}`}
                            className={f.alerta ? "bg-rose-50/60" : ""}>
                    <TableCell>
                      <div className="font-medium leading-tight">{f.frentista_nome || f.frentista_id}</div>
                      <div className="text-[10.5px] text-neutral-500">{f.unit_nome || "—"}</div>
                    </TableCell>
                    <TableCell className="text-center">{f.inscricoes ?? 0}</TableCell>
                    <TableCell className="text-center">
                      {f.media_colegas == null ? "—" : Number(f.media_colegas).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}
                    </TableCell>
                    <TableCell className="text-center">{textoRazao(f.razao)}</TableCell>
                    <TableCell className="text-center">
                      <span data-testid={`rota-frentista-alerta-${f.frentista_id}`}
                            className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide whitespace-nowrap ${f.alerta ? "bg-rose-100 text-rose-800" : "bg-emerald-50 text-emerald-700"}`}>
                        {f.alerta ? "Acima da média" : "Normal"}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
                {atual && frentistas.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center text-neutral-500 py-5">Nenhuma inscrição nos últimos {janelaAlerta} dias.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </section>
      </div>

      {/* Valores configurados */}
      <section className="card-soft rounded-xl p-4 space-y-2" data-testid="rota-valores">
        <div className="flex flex-wrap items-center gap-2">
          <Settings size={15} className="text-[color:var(--brand-blue)]" />
          <h2 className="font-serif-display text-lg mr-auto">Valores da Rota</h2>
          <Link to={{ pathname: "/master/descontos", hash: "#rota-fidelizacao" }} data-testid="rota-link-normal"
                className="btn-ghost h-9 px-3 text-[11px] font-medium inline-flex items-center gap-1.5">
            <Settings size={12} /> Configurar (Normal)
          </Link>
          <Link to={{ pathname: "/master/app-driver", hash: "#rota-fidelizacao" }} data-testid="rota-link-app-driver"
                className="btn-ghost h-9 px-3 text-[11px] font-medium inline-flex items-center gap-1.5">
            <Car size={12} /> Configurar (Motorista de App)
          </Link>
        </div>
        <p className="text-[11px] text-neutral-500">
          Desconto de cada etapa em centavos por litro (a mais do preço da categoria), prêmio em R$ e valor mínimo do
          abastecimento para avançar de etapa, por unidade e categoria. Valor vazio vale 0. Sem nenhum valor (etapas e
          prêmio em 0) a Rota não vale naquela unidade e categoria: o cliente segue as regras normais.
        </p>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-neutral-50">
              <TableHead className="text-[10px] uppercase tracking-widest">Unidade</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Categoria</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Etapa 1</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Etapa 2</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Etapa 3</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Prêmio</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Mínimo</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(valores || []).map((v) => (
                <TableRow key={`${v.unit_id}-${v.categoria}`} data-testid={`rota-valor-${v.unit_id}-${v.categoria}`}>
                  <TableCell className="font-medium">{v.unit_nome}</TableCell>
                  <TableCell className="text-[12px]">
                    {CATEGORIA_LBL[v.categoria] || v.categoria}
                    {!(v.etapa1 > 0 || v.etapa2 > 0 || v.etapa3 > 0 || v.premio > 0) && (
                      <span data-testid={`rota-valor-sem-${v.unit_id}-${v.categoria}`}
                            className="ml-1.5 inline-block px-1.5 py-0.5 rounded-md text-[9.5px] font-bold uppercase tracking-wide bg-neutral-100 text-neutral-600 whitespace-nowrap">
                        Sem valores: não vale
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{centavosLitro(v.etapa1)}</TableCell>
                  <TableCell className="whitespace-nowrap">{centavosLitro(v.etapa2)}</TableCell>
                  <TableCell className="whitespace-nowrap">{centavosLitro(v.etapa3)}</TableCell>
                  <TableCell className="whitespace-nowrap">{reais(v.premio)}</TableCell>
                  <TableCell className="whitespace-nowrap">{reais(v.minimo)}</TableCell>
                </TableRow>
              ))}
              {valores && valores.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-neutral-500 py-5">Nenhuma configuração encontrada.</TableCell></TableRow>
              )}
              {!valores && (
                <TableRow><TableCell colSpan={7} className="text-center text-neutral-500 py-5">Carregando...</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </section>

      {/* Clientes por etapa */}
      <section className="space-y-2" data-testid="rota-clientes">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1 p-1 bg-white/80 border border-neutral-200/70 rounded-lg w-fit overflow-x-auto max-w-full mr-auto" data-testid="rota-etapa-tabs">
            {ETAPAS_LISTA.map(([k, label]) => (
              <button key={k} data-testid={`rota-etapa-${k}`} onClick={() => setEtapa(k)}
                      className={`px-3.5 h-8 rounded-md text-[11.5px] font-medium whitespace-nowrap transition-colors ${etapa === k ? "bg-neutral-900 text-white" : "text-neutral-600 hover:bg-neutral-100"}`}>
                {label}
              </button>
            ))}
          </div>
          <button type="button" data-testid="rota-enviar-btn" onClick={() => setEnvioAberto(true)}
                  className="btn-ghost h-9 px-3 text-[11px] font-medium inline-flex items-center gap-1.5" style={{ borderRadius: 8 }}>
            <Send size={12} /> Enviar mensagem
          </button>
          <ExcelBtn testid="rota-clientes-excel" url={excelUrl} filename={`rota_fidelizacao_${etapa}.xlsx`} />
        </div>
        <p className="text-[11px] text-neutral-500" data-testid="rota-clientes-escopo">
          Situação atual de todos os inscritos (não segue o período de ingresso).
          {" "}Para enviar mensagem só a alguns clientes, marque-os; sem marcar, ela vai para toda a aba "{etapaLbl}".
        </p>
        {idsSelecionados.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-[12px]" data-testid="rota-selecao">
            <span><b data-testid="rota-selecao-n">{idsSelecionados.length}</b> {idsSelecionados.length === 1 ? "cliente selecionado" : "clientes selecionados"}</span>
            <button type="button" data-testid="rota-selecao-limpar" onClick={() => setSelecionados({})}
                    className="btn-ghost h-7 px-2.5 text-[11px] font-medium" style={{ borderRadius: 8 }}>
              Limpar seleção
            </button>
          </div>
        )}
        <div className="card-soft rounded-xl overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-neutral-50">
              <TableHead className="w-8">
                <input type="checkbox" data-testid="rota-sel-pagina" aria-label="Selecionar os clientes desta página"
                       checked={paginaToda} disabled={!lista.items.length}
                       onChange={(e) => marcar(lista.items, e.target.checked)} />
              </TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Cliente</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Categoria</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Situação</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Ingresso</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Último abastecimento</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Inscrito por</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Prêmio disponível</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {lista.items.map((c) => (
                <TableRow key={c.cliente_id} data-testid={`rota-cliente-${c.cliente_id}`}>
                  <TableCell className="w-8">
                    <input type="checkbox" data-testid={`rota-sel-${c.cliente_id}`} aria-label={`Selecionar ${c.nome || "cliente"}`}
                           checked={selecionados[c.cliente_id] !== undefined}
                           onChange={(e) => marcar([c], e.target.checked)} />
                  </TableCell>
                  <TableCell>
                    <div className="font-medium leading-tight">{c.nome || "—"}</div>
                    <div className="text-[10.5px] text-neutral-500">
                      <span className="font-mono uppercase">{c.placa || "—"}</span>{c.telefone ? ` · ${c.telefone}` : ""}
                    </div>
                  </TableCell>
                  <TableCell className="text-[12px]">{CATEGORIA_LBL[c.categoria] || c.categoria || "—"}</TableCell>
                  <TableCell>
                    <span className="chip chip-green text-[10px] py-0.5 font-bold uppercase tracking-wide">{rotuloEtapaCliente(c.etapa)}</span>
                  </TableCell>
                  <TableCell className="text-[12px] whitespace-nowrap">{fmtDia(c.ingresso_em)}</TableCell>
                  <TableCell className="text-[12px] whitespace-nowrap">{fmtDia(c.ultimo_dia)}</TableCell>
                  <TableCell className="text-[12px]">{c.inscrito_por_nome || "—"}</TableCell>
                  <TableCell className="whitespace-nowrap">{Number(c.saldo_rota_premio) > 0 ? reais(c.saldo_rota_premio) : "—"}</TableCell>
                </TableRow>
              ))}
              {lista.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-neutral-500 py-8">
                    Nenhum cliente em "{etapaLbl}".
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <Pagination page={lista.page || 1} totalPages={lista.total_pages || 0} total={lista.total || 0}
                    onPage={carregarLista} unitLabel="clientes" />
      </section>

      <OcorrenciasRota unitParam={unitParam} />

      <EnvioRotaDialog open={envioAberto} onOpenChange={setEnvioAberto} selecionados={idsSelecionados}
                       etapa={etapa} etapaLbl={etapaLbl} unitParam={unitParam} unidadeNome={unidadeNome}
                       onEnviado={() => { setEnvioAberto(false); setSelecionados({}); }} />
    </div>
  );
}

const inputCls = "w-full h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";

/**
 * Envio manual (verificação final da Fase 5, item 10). Público: os selecionados (qualquer etapa) ou
 * toda a aba da lista (etapa + unidade do topo + categorias). A prévia usa a MESMA seleção do envio
 * (backend) e diz quem fica de fora; o botão Enviar só libera com a prévia em dia e pede confirmação
 * com a contagem. O envio sai em segundo plano (SMS e WhatsApp pelos serviços de sempre: allowlist
 * no ambiente de teste e, na API oficial, só com o aceite do cliente).
 */
function EnvioRotaDialog({ open, onOpenChange, selecionados, etapa, etapaLbl, unitParam, unidadeNome, onEnviado }) {
  const [canal, setCanal] = useState("sms");
  const [texto, setTexto] = useState(TEXTO_ENVIO_ROTA_PADRAO);
  const [categorias, setCategorias] = useState(["normal", "app_driver"]);
  const [prev, setPrev] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [waStatus, setWaStatus] = useState(null);
  const msgRef = useRef(null);
  const seq = useRef(0);
  const chaveSel = selecionados.join(",");
  const chaveCat = categorias.join(",");

  // WhatsApp em uso (para a prévia dizer o que sai); falha aqui nunca bloqueia a janela.
  useEffect(() => {
    if (!open) return;
    Promise.resolve()
      .then(() => masterApi.get("/master/whatsapp/oficial/status"))
      .then((r) => setWaStatus(r?.data || null))
      .catch(() => setWaStatus(null));
  }, [open]);
  // Qualquer mudança no público, no canal ou no texto invalida a prévia (a contagem mudaria).
  useEffect(() => { seq.current += 1; setPrev(null); }, [open, canal, texto, chaveCat, chaveSel, etapa, unitParam]);

  const comSelecao = selecionados.length > 0;
  const erro = validarTextoEnvioRota(texto, temEmoji);
  const semCategoria = !comSelecao && !categorias.length;
  const temSms = canal === "sms" || canal === "ambos";
  const sms = temSms ? smsInfo(texto) : null;
  const estimativa = temSms ? estimativaSms(texto) : { aviso: null };
  const resumo = prev ? resumoPreview(prev, canal, waStatus) : null;
  const motivos = motivosExclusao(prev);
  const amostra = Array.isArray(prev?.amostra) ? prev.amostra.slice(0, 5) : [];
  const total = Number(prev?.total) || 0;
  const publico = textoPublicoEnvio({ selecionados: selecionados.length, etapa, unidadeNome });
  const corpo = () => corpoEnvioRota({ selecionados, etapa, unitId: unitParam, categorias, canal, texto });
  const podeEnviar = !!prev && !carregando && !enviando && !erro && !semCategoria && total > 0 && !resumo?.nadaSai;

  const alternarCategoria = (k) => setCategorias((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const inserirVariavel = (v) => {
    const el = msgRef.current;
    const r = inserirNoCursor(texto, `{${v}}`, el?.selectionStart, el?.selectionEnd);
    setTexto(r.texto);
    setTimeout(() => {
      try { if (el) { el.focus(); el.setSelectionRange(r.cursor, r.cursor); } } catch (_) { /* ignora */ }
    }, 0);
  };

  const verPrevia = async () => {
    if (erro) { modal.error(erro); return; }
    if (semCategoria) { modal.error("Escolha ao menos uma categoria de cliente."); return; }
    const minha = ++seq.current;
    setCarregando(true);
    try {
      const { data } = await masterApi.post("/master/rota-fidelizacao/enviar/preview", corpo());
      if (minha === seq.current) setPrev(data || {});
    } catch (e) { if (minha === seq.current) modal.error(erroEnvioRota(e, apiError)); }
    finally { setCarregando(false); }
  };

  const enviar = async () => {
    if (!podeEnviar) return;
    const fora = motivos.reduce((s, m) => s + m.n, 0);
    const ok = await modal.confirm({
      title: "Enviar mensagem aos clientes da Rota",
      message: "A mensagem começa a sair agora, em segundo plano (alguns segundos entre um cliente e outro). "
        + "Depois de confirmar não dá para cancelar. Acompanhe em Campanhas > Histórico de envios.",
      details: [
        { label: "Público", value: publico },
        { label: "Canal", value: CANAL_ENVIO_LBL[canal] || canal },
        { label: "Clientes que recebem", value: String(total) },
        ...(fora ? [{ label: "Ficam de fora", value: String(fora) }] : []),
      ],
      confirmText: "Enviar agora",
    });
    if (!ok) return;
    setEnviando(true);
    try {
      const { data } = await masterApi.post("/master/rota-fidelizacao/enviar", corpo());
      modal.success({
        title: "Envio iniciado",
        message: "As mensagens estão saindo em segundo plano. O resultado de cada uma aparece em Campanhas > "
          + "Histórico de envios (campanha \"Envios manuais da Rota\").",
        details: [{ label: "Clientes", value: String(data?.total ?? total) }],
      });
      if (onEnviado) onEnviado();
    } catch (e) { modal.error(erroEnvioRota(e, apiError)); }
    finally { setEnviando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl border-none p-0 max-h-[90vh] overflow-auto" {...protegeDoModalGlobal}>
        <div className="bg-white p-5 rounded-2xl space-y-3" data-testid="rota-envio">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-xl pr-6">Enviar mensagem · Rota de Fidelização</DialogTitle>
          </DialogHeader>
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-[12px]" data-testid="rota-envio-publico">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Público</div>
            <div className="font-medium" data-testid="rota-envio-publico-texto">{publico}</div>
            <div className="text-[10.5px] text-neutral-500 mt-0.5">
              {comSelecao
                ? "Só os clientes marcados na lista (qualquer etapa)."
                : `Todos da aba "${etapaLbl}"${unidadeNome ? "" : ", de todas as unidades"}. Para escolher clientes, marque-os na lista.`}
              {" "}Quem está inativo, recusou receber ofertas ou não tem telefone fica de fora; com a Rota desligada, também quem está nas etapas.
            </div>
          </div>

          {!comSelecao && (
            <Field label="Categorias de cliente">
              <div className="flex flex-wrap gap-1.5" data-testid="rota-envio-categorias">
                {CATEGORIAS.map(([k, lbl]) => {
                  const on = categorias.includes(k);
                  return (
                    <button key={k} type="button" data-testid={`rota-envio-cat-${k}`} aria-pressed={on} onClick={() => alternarCategoria(k)}
                            className={`h-9 px-3 text-[12px] font-medium ${on ? "btn-primary-blue" : "btn-ghost"}`} style={{ borderRadius: 8 }}>
                      {lbl}
                    </button>
                  );
                })}
              </div>
              {semCategoria && <p className="text-[11px] text-rose-700 mt-1" data-testid="rota-envio-cat-erro">Escolha ao menos uma categoria.</p>}
            </Field>
          )}

          <Field label="Canal de envio">
            <select data-testid="rota-envio-canal" value={canal} onChange={(e) => setCanal(e.target.value)} className={inputCls}>
              <option value="sms">SMS</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="ambos">SMS + WhatsApp</option>
            </select>
            {canal !== "sms" && (
              <p className="text-[10.5px] text-amber-800 mt-1" data-testid="rota-envio-canal-aviso">
                Com a API oficial da Meta em uso, mensagem promocional por WhatsApp só sai depois do aceite do cliente. Confira na prévia.
              </p>
            )}
          </Field>

          <Field label="Mensagem">
            <textarea ref={msgRef} data-testid="rota-envio-texto" value={texto} onChange={(e) => setTexto(e.target.value)} rows={4}
                      className={`w-full rounded-lg bg-white border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/15 transition ${erro ? "border-rose-400" : "border-emerald-400 focus:border-[color:var(--brand-blue)]"}`} />
            <div className="flex flex-wrap items-center gap-1 mt-1" data-testid="rota-envio-variaveis">
              <span className="text-[10.5px] text-neutral-500 mr-1">Variáveis:</span>
              {VARIAVEIS_ENVIO_ROTA.map((v) => (
                <button key={v} type="button" data-testid={`rota-envio-var-${v}`} title={VARIAVEIS_ENVIO_ROTA_DESCRICAO[v]}
                        onClick={() => inserirVariavel(v)}
                        className="px-1.5 h-6 rounded-md border border-neutral-200 bg-white text-[10.5px] font-mono hover:bg-neutral-50">
                  {`{${v}}`}
                </button>
              ))}
            </div>
            <ul className="mt-1 text-[10.5px] text-neutral-500 grid sm:grid-cols-2 gap-x-3">
              {VARIAVEIS_ENVIO_ROTA.map((v) => <li key={v}><span className="font-mono">{`{${v}}`}</span>: {VARIAVEIS_ENVIO_ROTA_DESCRICAO[v]}</li>)}
            </ul>
            {erro && <p className="text-[11.5px] text-rose-700 mt-1" data-testid="rota-envio-erro">{erro}</p>}
            {sms && (
              <div data-testid="rota-envio-sms-len" className={`text-[10px] mt-1 ${sms.passou ? "text-rose-600 font-semibold" : "text-neutral-500"}`}>
                SMS (sem acentos): {textoTamanhoSms(sms.caracteres)}{sms.passou ? "" : " · as variáveis aumentam o tamanho final"}
              </div>
            )}
            {sms && estimativa.aviso && (
              <p className="text-[11px] text-amber-800 mt-1" data-testid="rota-envio-sms-estimativa">{estimativa.aviso}</p>
            )}
          </Field>

          <button type="button" data-testid="rota-envio-previa" onClick={verPrevia} disabled={carregando || !!erro || semCategoria}
                  className="btn-ghost h-9 px-3 text-[12px] font-medium w-full" style={{ borderRadius: 8 }}>
            {carregando ? "Calculando..." : "Ver prévia (quem recebe)"}
          </button>

          {prev && (
            <div className="space-y-2" data-testid="rota-envio-prev">
              <div>
                <div className={`font-serif-display text-3xl ${resumo.nadaSai ? "text-neutral-400" : ""}`} data-testid="rota-envio-total">{total}</div>
                <div className={`text-xs ${resumo.nadaSai || !total ? "text-rose-700 font-semibold" : "text-neutral-500"}`} data-testid="rota-envio-legenda">
                  {resumo.nadaSai
                    ? "clientes no público, mas NENHUM envio sai agora: só WhatsApp e a API oficial da Meta não envia promoção sem o aceite do cliente. Troque o canal para SMS."
                    : total
                      ? `${total === 1 ? "cliente recebe" : "clientes recebem"} esta mensagem (${CANAL_ENVIO_LBL[canal] || canal}).`
                      : "Ninguém recebe esta mensagem. Veja abaixo quem fica de fora."}
                </div>
              </div>
              {resumo.avisos.length > 0 && (
                <div className="space-y-1.5" data-testid="rota-envio-avisos">
                  {resumo.avisos.map((a) => (
                    <div key={a} className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-900">
                      <AlertTriangle size={12} className="shrink-0 mt-0.5" /><span>{a}</span>
                    </div>
                  ))}
                </div>
              )}
              {motivos.length > 0 && (
                <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2" data-testid="rota-envio-motivos">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Ficam de fora</div>
                  <ul className="text-[11.5px] space-y-0.5">
                    {motivos.map((m) => <li key={m.chave} data-testid={`rota-envio-motivo-${m.chave}`}>{m.rotulo}: <b>{m.n}</b></li>)}
                  </ul>
                </div>
              )}
              {amostra.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Amostra (como sai)</div>
                  <ul className="text-xs space-y-1">
                    {amostra.map((c, i) => (
                      <li key={i} data-testid={`rota-envio-amostra-${i}`}>
                        <div>{c.nome} — {typeof formatPhone === "function" ? formatPhone(c.telefone) : c.telefone}</div>
                        {c.mensagem && (
                          <div className="text-[11px] text-neutral-500 whitespace-pre-wrap break-words">
                            {canal === "ambos" && <span className="font-semibold text-neutral-600">WhatsApp: </span>}{c.mensagem}
                          </div>
                        )}
                        {canal === "ambos" && c.mensagem_sms && (
                          <div className="text-[11px] text-neutral-500 whitespace-pre-wrap break-words">
                            <span className="font-semibold text-neutral-600">SMS (como sai): </span>{c.mensagem_sms}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <button type="button" data-testid="rota-envio-enviar" onClick={enviar} disabled={!podeEnviar}
                  className="btn-action btn-salvar w-full">
            <Send size={15} /> {enviando ? "Enviando..." : (prev ? `Enviar para ${total} ${total === 1 ? "cliente" : "clientes"}` : "Veja a prévia antes de enviar")}
          </button>
          <p className="text-[10.5px] text-neutral-500">
            SMS sai sem acentos e cortado em {SMS_LIMITE} caracteres. Sem emojis.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

const ATALHOS_OCORRENCIAS = ATALHOS.filter(([k]) => k !== "tudo").concat([["tudo", "Todas"]]);

/**
 * Ocorrências da Rota (verificação final das Fases 3 e 4): as vendas finalizadas com o desconto da
 * Rota já concedido num 2º uso no mesmo dia, ou com o prêmio já usado/saldo menor, para o Master
 * auditar (frentista, cliente, valor concedido). Período = data da VENDA, abre em Hoje.
 * GET /master/rota-fidelizacao/ocorrencias?unit_id&inicio&fim&page (+ /excel).
 */
function OcorrenciasRota({ unitParam }) {
  const [atalho, setAtalho] = useState("hoje");
  const [dados, setDados] = useState({ items: [], total: 0, page: 1, total_pages: 0, total_desconto_reais: 0 });
  const [carregado, setCarregado] = useState(false);
  const qs = () => {
    const p = new URLSearchParams();
    if (unitParam) p.set("unit_id", unitParam);
    if (atalho !== "tudo") {
      const a = atalhoPeriodoBR(atalho);
      const ini = inicioDiaBR(a.inicio);
      const fi = fimDiaBR(a.fim);
      if (ini) p.set("inicio", ini);
      if (fi) p.set("fim", fi);
    }
    return p.toString();
  };
  const seq = useRef(0);
  const carregar = async (page = 1) => {
    const minha = ++seq.current;
    try {
      const q = qs();
      const r = await masterApi.get(comPagina(`/master/rota-fidelizacao/ocorrencias${q ? `?${q}` : ""}`, page));
      if (minha !== seq.current) return;
      const pg = normalizaPaginado(r.data, page);
      setDados({ items: pg.items, total: pg.total, page: pg.page, total_pages: pg.total_pages,
        total_desconto_reais: Number(r?.data?.total_desconto_reais) || 0 });
      setCarregado(true);
    } catch (e) { if (minha === seq.current) modal.error(apiError(e)); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(1); }, [unitParam, atalho]);
  const q = qs();
  const excelUrl = `/master/rota-fidelizacao/ocorrencias/excel${q ? `?${q}` : ""}`;

  return (
    <section className="space-y-2" data-testid="rota-ocorrencias-lista">
      <div className="flex flex-wrap items-center gap-2">
        <AlertTriangle size={15} className="text-amber-600" />
        <h2 className="font-serif-display text-lg mr-auto">Ocorrências da Rota</h2>
        <ExcelBtn testid="rota-ocorrencias-excel" url={excelUrl} filename="rota_fidelizacao_ocorrencias.xlsx" />
      </div>
      <p className="text-[11px] text-neutral-500">
        Vendas finalizadas com o desconto da Rota já concedido quando a regra não permitia (2º uso no mesmo dia, prêmio
        já usado ou saldo menor que o abatido). A venda não trava na pista; confira aqui o frentista e o cliente.
        Período pela data da venda.
      </p>
      <div className="flex flex-wrap gap-1" data-testid="rota-ocorrencias-atalhos">
        {ATALHOS_OCORRENCIAS.map(([k, lbl]) => (
          <button key={k} data-testid={`rota-ocorrencias-atalho-${k}`} onClick={() => setAtalho(k)}
                  className={`h-9 px-2.5 text-[11px] font-medium ${atalho === k ? "btn-primary-blue" : "btn-ghost"}`}>
            {lbl}
          </button>
        ))}
      </div>
      {carregado && dados.total > 0 && (
        <div className="text-[12px] text-amber-900" data-testid="rota-ocorrencias-resumo">
          <b>{dados.total}</b> venda{dados.total > 1 ? "s" : ""} com ocorrência · desconto concedido nelas: <b>{reais(dados.total_desconto_reais)}</b>
        </div>
      )}
      <div className="card-soft rounded-xl overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-neutral-50">
            <TableHead className="text-[10px] uppercase tracking-widest">Venda</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest">Frentista</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest">Cliente</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest">Desconto</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest">Concedido</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest">Ocorrência</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {dados.items.map((o) => (
              <TableRow key={o.atendimento_id} data-testid={`rota-ocorrencia-${o.atendimento_id}`}>
                <TableCell className="text-[12px] whitespace-nowrap">
                  <div>{fmtDataHora(o.criado_em)}</div>
                  <div className="text-[10.5px] text-neutral-500">{o.unit_nome || "—"}</div>
                </TableCell>
                <TableCell className="text-[12px]">{o.frentista_nome || "—"}</TableCell>
                <TableCell>
                  <div className="font-medium leading-tight text-[12px]">{o.cliente_nome || "—"}</div>
                  <div className="text-[10.5px] text-neutral-500 font-mono uppercase">{o.placa || "—"}</div>
                </TableCell>
                <TableCell className="text-[12px] whitespace-nowrap">{o.desconto_label || o.desconto_tipo || "—"}</TableCell>
                <TableCell className="text-[12px] whitespace-nowrap font-semibold">{reais(o.desconto_total_reais)}</TableCell>
                <TableCell className="text-[11px] text-neutral-700 min-w-[220px]">{o.rota_ocorrencia || "—"}</TableCell>
              </TableRow>
            ))}
            {carregado && dados.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-neutral-500 py-6" data-testid="rota-ocorrencias-vazio">
                  Nenhuma ocorrência no período.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Pagination page={dados.page || 1} totalPages={dados.total_pages || 0} total={dados.total || 0}
                  onPage={carregar} unitLabel="vendas" />
    </section>
  );
}

function Kpi({ label, value, sub, highlight, testid }) {
  return (
    <div data-testid={testid} className="card-soft rounded-xl p-3"
         style={highlight ? { background: "linear-gradient(135deg,#ECFDF5 0%,#D1FAE5 100%)", borderColor: "#6EE7B7" } : undefined}>
      <div className="text-[9.5px] uppercase tracking-widest text-neutral-500 leading-tight">{label}</div>
      <div data-testid={testid ? `${testid}-valor` : undefined} className="font-display font-black text-2xl leading-none mt-1 text-[color:var(--ink)] number-tabular">{value}</div>
      {sub && <div className="text-[10.5px] text-neutral-500 mt-1 leading-tight">{sub}</div>}
    </div>
  );
}

function FunilPasso({ label, valor, pct, cor, testid }) {
  const corBarra = {
    emerald: "bg-emerald-400 border-emerald-500",
    amber: "bg-amber-300 border-amber-400",
    blue: "bg-blue-400 border-blue-500",
  }[cor] || "bg-neutral-300 border-neutral-400";
  // Rótulo, valor e % numa linha; embaixo, o trilho com a barra em width = pct%. A barra não leva o
  // rótulo dentro (nem flex-1, cujo flex-basis 0 anulava a largura): assim a proporção aparece
  // também no celular, onde o rótulo não caberia numa barra curta.
  const largura = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[12px] font-medium leading-tight text-neutral-700 min-w-0">{label}</span>
        <span className="text-[12px] whitespace-nowrap shrink-0">
          <b className="number-tabular" data-testid={`${testid}-valor`}>{valor}</b>
          <span className="text-[11px] text-neutral-500 ml-1.5 number-tabular" data-testid={`${testid}-pct`}>{pct}%</span>
        </span>
      </div>
      <div className="mt-1 h-3 w-full rounded-md bg-neutral-100 border border-neutral-200 overflow-hidden" data-testid={`${testid}-trilho`}>
        <div className={`h-full rounded-md ${largura > 0 ? `border-r ${corBarra}` : ""}`} data-testid={`${testid}-barra`}
             style={{ width: `${largura}%` }} />
      </div>
    </div>
  );
}
